import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { UserSharedService } from '../core/users/user.service';

export interface BottomNavItem {
    label: string;
    icon: string;
    routerLink?: string;   // undefined when the item is a parent-only node
    exact?: boolean;
    children?: BottomNavItem[];
}

@Component({
    selector: 'app-bottom-nav',
    templateUrl: './app.bottom-nav.component.html',
    styleUrls: ['./app.bottom-nav.component.scss']
})
export class AppBottomNavComponent implements OnInit, OnDestroy {

    /** All permitted nav items shown in the scrollable bar */
    allItems: BottomNavItem[] = [];

    /** Currently-open submenu sheet, or null */
    openSubmenu: BottomNavItem | null = null;

    activeRoute = '';
    private routerSub!: Subscription;

    constructor(
        private userService: UserSharedService,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.buildNav();
        this.activeRoute = this.router.url;
        this.routerSub = this.router.events
            .pipe(filter(e => e instanceof NavigationEnd))
            .subscribe((e: any) => {
                this.activeRoute = e.urlAfterRedirects;
                this.openSubmenu = null;
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.routerSub?.unsubscribe();
    }

    private buildNav(): void {
        const isSuperAdmin = localStorage.getItem('userRole') === 'super-admin';
        const p = (perm: string) => this.userService.hasSchoolPermission(perm);

        const all: BottomNavItem[] = [
            { label: 'Dashboard',  icon: 'wow wow-dashboard',           routerLink: '/dashboard',             exact: true },
            {
                label: 'Schools',
                icon: 'wow wow-classroom',
                routerLink: '/schools',
            },
            { label: 'Classrooms', icon: 'wow wow-classroom',            routerLink: '/classrooms' },
            { label: 'Requests',   icon: 'wow wow-inv-request',          routerLink: '/requests' },
            { label: 'Stocks',     icon: 'wow wow-inv-stocks',           routerLink: '/stocks' },
            {
                label: 'Products',
                icon: 'wow wow-wowcare-shopping-bag',
                children: [
                    ...(p('VIEW_PRODUCT_CATEGORY') ? [{ label: 'Categories', icon: 'wow wow-wowcare-shopping-bag', routerLink: '/products/product-category' }] : []),
                    { label: 'Product List', icon: 'wow wow-wowcare-shopping-bag', routerLink: '/products/product-list' },
                ],
            },
            {
                label: 'Vendors',
                icon: 'wow wow-inv-person',
                children: [
                    ...(p('VENDOR_CATEGORY_VIEW') ? [{ label: 'Category', icon: 'wow wow-inv-person', routerLink: '/vendors/vendor-category' }] : []),
                    { label: 'Vendors', icon: 'wow wow-inv-person', routerLink: '/vendors/vendor-list' },
                ],
            },
            { label: 'Purchase',   icon: 'wow wow-inv-purchase',         routerLink: '/purchase' },
            { label: 'Reports',    icon: 'wow wow-inv-report',           routerLink: '/reports' },
            { label: 'Users',      icon: 'wow wow-staff',                routerLink: '/users' },
            { label: 'Settings',   icon: 'wow wow-inv-settings',         routerLink: '/profile/user' },
        ];

        const visible: Record<string, boolean> = {
            '/dashboard':             p('DASHBOARD_ADMIN') || p('DASHBOARD_STAFF'),
            '/schools':               isSuperAdmin,
            '/classrooms':            p('CLASSROOM_VIEW'),
            '/requests':              p('REQUEST_VIEW'),
            '/stocks':                p('STOCK_VIEW'),
            'Products':               p('PRODUCT_VIEW'),
            'Vendors':                p('VENDOR_VIEW'),
            '/purchase':              p('PURCHASE_VIEW'),
            '/reports':               p('STOCK_VIEW'),
            '/users':                 p('STAFF_VIEW'),
            '/profile/user':          true,
        };

        this.allItems = all.filter(item => {
            const key = item.routerLink ?? item.label;
            return visible[key] ?? false;
        });
    }

    /** True when this item (or one of its children) matches the active route */
    isActive(item: BottomNavItem): boolean {
        if (item.children?.length) {
            return item.children.some(c => c.routerLink && this.activeRoute.startsWith(c.routerLink));
        }
        if (!item.routerLink) return false;
        if (item.exact) return this.activeRoute === item.routerLink;
        return this.activeRoute.startsWith(item.routerLink);
    }

    onItemTap(item: BottomNavItem): void {
        if (item.children?.length) {
            // Toggle submenu sheet
            this.openSubmenu = this.openSubmenu === item ? null : item;
        } else if (item.routerLink) {
            this.openSubmenu = null;
            this.router.navigateByUrl(item.routerLink);
        }
    }

    navigate(link: string): void {
        this.openSubmenu = null;
        this.router.navigateByUrl(link);
    }

    closeSubmenu(): void {
        this.openSubmenu = null;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.openSubmenu = null;
    }
}
